// https://medium.com/swlh/using-react-hooks-to-sync-your-component-state-with-the-url-query-string-81ccdfcb174f

import { default as qs } from 'query-string';
import { useCallback, useEffect, useState } from 'react';

export const useQueryString = (key: string, initialValue?: any, addHistoryRecord?: boolean) => {
  const [value, setValue] = useState(getQueryStringValue(key) || initialValue);

  const onSetValue = useCallback(
    (newValue: any, addHistoryRecordOverride?: boolean) => {
      setValue(newValue);
      setQueryStringValue(key, newValue, addHistoryRecordOverride ?? addHistoryRecord);
    },
    [key, addHistoryRecord],
  );

  useEffect(() => {
    const handlePopState = () => setValue(getQueryStringValue(key) || initialValue);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [key, initialValue]);

  return [value, onSetValue];
};

export default useQueryString;

const prepareHref = (qsValue: string) => {
  const base = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  return qsValue ? `${base}?${qsValue}` : base;
};

const setQueryStringWithoutPageReload = (qsValue: string) => {
  const newurl = prepareHref(qsValue);
  window.history.replaceState({ path: newurl }, '', newurl);
};

const setQueryStringWithHistoryChange = (qsValue: string) => {
  const newurl = prepareHref(qsValue);
  window.history.pushState({ path: newurl }, '', newurl);
};

export const setQueryStringValue = (key: string, value: any, addHistoryRecord: boolean) => {
  const values = qs.parse(window.location.search);

  const newValues = { ...values };
  if (value === '' || value === null || value === undefined) {
    delete newValues[key];
  } else {
    newValues[key] = value;
  }
  const newQsValue = qs.stringify(newValues);
  if (addHistoryRecord) {
    setQueryStringWithHistoryChange(newQsValue);
  } else {
    setQueryStringWithoutPageReload(newQsValue);
  }
};

export const getQueryStringValue = (key: string) => {
  const values = qs.parse(window.location.search);
  return values[key];
};
